/**
 * DHT11/DHT22 Sensor Simulator for avr8js
 * 
 * Simulates the single-wire protocol used by DHT sensors.
 */

const CPU_FREQ = 16e6; // 16 MHz

// Convert microseconds to CPU clock cycles
const usToClocks = (us) => Math.round((us / 1e6) * CPU_FREQ);

// State machine states
const S = {
    IDLE: 0,            // Waiting for MCU start signal
    WAIT_START_END: 1,  // MCU is holding LOW, waiting for release
    SENSOR_ACK_LOW: 2,  // Sensor pulls LOW ~80µs
    SENSOR_ACK_HIGH: 3, // Sensor goes HIGH ~80µs
    BIT_LOW: 4,         // LOW phase of each bit ~50µs
    BIT_HIGH: 5,        // HIGH phase (duration encodes 0 or 1)
    DONE_LOW: 6,        // Final LOW pulse ~50µs
    FINISHED: 7,        // Transmission complete, back to idle
};

export class DHTSimulator {
    constructor(type = 'DHT11') {
        this.type = type;
        this.temperature = 25.0;
        this.humidity = 60.0;

        this.state = S.IDLE;
        this.stateStartCycle = 0;
        this.bits = [];
        this.bitIndex = 0;

        // Track the MCU's previous output state for edge detection
        this.prevMcuDriving = false;
        this.prevMcuHigh = true;
        this.startLowCycle = 0;
    }

    setValues(temp, humidity) {
        this.temperature = temp;
        this.humidity = humidity;
    }

    reset() {
        this.state = S.IDLE;
        this.stateStartCycle = 0;
        this.bits = [];
        this.bitIndex = 0;
        this.prevMcuDriving = false;
        this.prevMcuHigh = true;
        this.startLowCycle = 0;
    }

    /**
     * Build the 40-bit payload from current temp/humidity values
     */
    _buildPayload() {
        let humHi, humLo, tempHi, tempLo;

        if (this.type === 'DHT11') {
            humHi = Math.max(0, Math.min(99, Math.round(this.humidity)));
            humLo = 0;
            tempHi = Math.max(0, Math.min(50, Math.round(this.temperature)));
            tempLo = 0;
        } else {
            // DHT22: 16-bit values, humidity × 10, temp × 10 with sign bit
            const humVal = Math.max(0, Math.min(999, Math.round(this.humidity * 10)));
            humHi = (humVal >> 8) & 0xFF;
            humLo = humVal & 0xFF;

            let tempVal = Math.round(Math.abs(this.temperature) * 10);
            tempVal = Math.min(tempVal, 799);
            if (this.temperature < 0) tempVal |= 0x8000;
            tempHi = (tempVal >> 8) & 0xFF;
            tempLo = tempVal & 0xFF;
        }

        const checksum = (humHi + humLo + tempHi + tempLo) & 0xFF;
        const bytes = [humHi, humLo, tempHi, tempLo, checksum];

        // Convert 5 bytes → 40 bits (MSB first)
        const bits = [];
        for (const byte of bytes) {
            for (let b = 7; b >= 0; b--) {
                bits.push((byte >> b) & 1);
            }
        }
        return bits;
    }

    /**
     * Called every CPU cycle during simulation.
     */
    tick(cycles, mcuDriving, mcuHigh) {
        const elapsed = cycles - this.stateStartCycle;

        switch (this.state) {
            case S.IDLE: {
                // Detect MCU pulling line LOW (output mode, LOW)
                if (mcuDriving && !mcuHigh) {
                    if (!this.prevMcuDriving || this.prevMcuHigh) {
                        // Falling edge — MCU just started the start signal
                        this.startLowCycle = cycles;
                    }
                }
                // Detect MCU releasing line after holding LOW for ≥ 18ms (or ≥1ms for tolerance)
                if (this.startLowCycle > 0 && (!mcuDriving || mcuHigh)) {
                    const lowDuration = cycles - this.startLowCycle;
                    if (lowDuration >= usToClocks(1000)) {
                        // Valid start signal detected! Begin sensor response
                        this.bits = this._buildPayload();
                        this.bitIndex = 0;
                        this.state = S.SENSOR_ACK_LOW;
                        this.stateStartCycle = cycles;
                        this.startLowCycle = 0;
                    } else if (mcuDriving && mcuHigh) {
                        // MCU drove HIGH too early — could be a false start, but let's be lenient
                        // Only reset if it was way too short
                        if (lowDuration < usToClocks(100)) {
                            this.startLowCycle = 0;
                        }
                    }
                }
                this.prevMcuDriving = mcuDriving;
                this.prevMcuHigh = mcuHigh;
                return null; // Hi-Z, not driving
            }

            case S.SENSOR_ACK_LOW: {
                // Sensor pulls LOW for ~80µs
                if (elapsed >= usToClocks(80)) {
                    this.state = S.SENSOR_ACK_HIGH;
                    this.stateStartCycle = cycles;
                }
                return false; // Drive LOW
            }

            case S.SENSOR_ACK_HIGH: {
                // Sensor goes HIGH for ~80µs
                if (elapsed >= usToClocks(80)) {
                    this.state = S.BIT_LOW;
                    this.stateStartCycle = cycles;
                }
                return true; // Drive HIGH
            }

            case S.BIT_LOW: {
                // LOW phase: ~50µs before each bit
                if (elapsed >= usToClocks(50)) {
                    this.state = S.BIT_HIGH;
                    this.stateStartCycle = cycles;
                }
                return false; // Drive LOW
            }

            case S.BIT_HIGH: {
                // HIGH phase: 26µs for "0", 70µs for "1"
                const bit = this.bits[this.bitIndex];
                const highDuration = bit ? usToClocks(70) : usToClocks(26);

                if (elapsed >= highDuration) {
                    this.bitIndex++;
                    if (this.bitIndex >= 40) {
                        this.state = S.DONE_LOW;
                    } else {
                        this.state = S.BIT_LOW;
                    }
                    this.stateStartCycle = cycles;
                }
                return true; // Drive HIGH
            }

            case S.DONE_LOW: {
                // Final LOW pulse ~50µs
                if (elapsed >= usToClocks(50)) {
                    this.state = S.FINISHED;
                    this.stateStartCycle = cycles;
                }
                return false; // Drive LOW
            }

            case S.FINISHED: {
                // Release line, go back to IDLE
                this.state = S.IDLE;
                this.startLowCycle = 0;
                this.prevMcuDriving = false;
                this.prevMcuHigh = true;
                return null; // Hi-Z
            }

            default:
                return null;
        }
    }
}
